import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useAuth } from '@/contexts/AuthContext';
import { useSearch } from '@/contexts/SearchContext';
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useState } from 'react';
import { Appointment, AppointmentStatus } from '@/types/types';
import { addDays, format } from 'date-fns';
import { DatePicker } from "@/components/ui/date-filter";
import { formatIstCalendarDateForApi, formatTime } from '@/utils/dateUtils';
import { appointmentApi } from '@/api/appointment';
import ViewDiagnosisRecordButton from '../consultation/viewDiagnosisRecord';
import { isDiagnosisWithinEditWindow } from '@/utils/diagnosisEditWindow';
import { notificationApi } from '@/api/notification';
import { toast } from 'sonner';
import { Loader2, MessageCircle } from 'lucide-react';

function Appointments() {
  const { user } = useAuth();
  const { searchQuery } = useSearch();
  const [activeTab, setActiveTab] = useState('upcoming');
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [whatsAppSendingId, setWhatsAppSendingId] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const date = formatIstCalendarDateForApi(selectedDate);
  const todayKey = formatIstCalendarDateForApi(new Date());
  const isToday = date === todayKey;

  // Use React Query for data fetching
  const { data: appointmentsData, isLoading } = useQuery({
    queryKey: ['appointments', user?.id, date],
    queryFn: async () => {
      const response = await appointmentApi.getAppointmentsByDateAndDoctor({
        doctorId: user?.id,
        date: date
      });
      return response.data?.data || [];
    },
    enabled: !!user?.id,
    refetchInterval: isToday ? 30000 : false,
    refetchOnWindowFocus: true,
    staleTime: 10000 // Consider data fresh for 10 seconds
  });

  // Handle consultation start
  const handleStartConsultation = () => {
    // Set up a one-time check after expected consultation completion
    setTimeout(() => {
      queryClient.invalidateQueries({ queryKey: ['appointments', user?.id, date] });
    }, 1000); // Check after 1 second
  };

  const handleSendWhatsApp = async (appointment: Appointment) => {
    const phone = appointment.patient?.phone?.trim();
    if (!phone) {
      toast.error('Patient has no phone number on file');
      return;
    }

    setWhatsAppSendingId(appointment.id);
    try {
      await notificationApi.sendPrescriptionByAppointment(appointment.id);
      toast.success(`Prescription sent to ${phone} on WhatsApp`);
    } catch (error: any) {
      const msg = error?.response?.data?.message || '';
      if (msg.includes('No prescription found')) {
        try {
          await notificationApi.sendDiagnosisRecord(appointment.id);
          toast.success(`Consultation report sent to ${phone} on WhatsApp`);
        } catch (inner: any) {
          toast.error(inner?.response?.data?.message || 'Failed to send on WhatsApp');
        }
      } else {
        toast.error(msg || 'Failed to send on WhatsApp');
      }
    } finally {
      setWhatsAppSendingId(null);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg">Loading...</div>
      </div>
    );
  }

  const filteredAppointments = appointmentsData?.filter((appointment: Appointment) => {
    if (searchQuery === '') { return true; }
    return (
      appointment?.patient?.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      appointment?.status?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      appointment?.visitType?.toLowerCase().includes(searchQuery.toLowerCase())
    );
  });
  // Separate appointments into upcoming and completed (sorted earliest first for the day)
  const sortByScheduledAsc = (a: Appointment, b: Appointment) =>
    new Date(a.scheduledAt).getTime() - new Date(b.scheduledAt).getTime();

  const upcomingAppointments = (filteredAppointments?.filter(
    (appointment: Appointment) =>
      appointment.status !== AppointmentStatus.DIAGNOSED
  ) || []).sort(sortByScheduledAsc);

  const completedAppointments = (filteredAppointments?.filter(
    (appointment: Appointment) =>
      appointment.status === AppointmentStatus.DIAGNOSED
  ) || []).sort(sortByScheduledAsc);


  const AppointmentTable = ({ appointments, title }: { appointments: Appointment[], title: string }) => (
    <div className="mb-8">
      <h2 className="text-xl font-semibold text-gray-800 mb-4">{title}</h2>
      <div className="rounded-lg">
        <Table>
          <TableHeader>
            <TableRow className="bg-blue-50">
              <TableHead className="text-gray-600">Patient Name</TableHead>
              <TableHead className="text-gray-600">Scheduled At</TableHead>
              <TableHead className="text-gray-600">Visit Type</TableHead>
              <TableHead className="text-gray-600">Status</TableHead>
              <TableHead className="text-gray-600">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {appointments.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-8 text-gray-500">
                  No appointments scheduled for this date
                </TableCell>
              </TableRow>
            ) : (
              appointments.map((appointment) => {
                const canEditDiagnosis =
                  appointment.status === AppointmentStatus.DIAGNOSED &&
                  isDiagnosisWithinEditWindow(appointment.diagnosisRecord?.createdAt);
                const isSendingWa = whatsAppSendingId === appointment.id;

                return (
                <TableRow key={appointment.id} className="border-b border-gray-50 bg-gray-50">
                  <TableCell className="font-medium">
                    <Link to={`/doctor/consultation/${appointment?.patient?.id}/${appointment.id}`} className="underline" target="_blank" rel="noopener noreferrer">
                      {appointment?.patient?.name}
                    </Link>
                  </TableCell>
                  <TableCell>{formatTime(new Date(appointment.scheduledAt))}</TableCell>
                  <TableCell>{appointment.visitType}</TableCell>
                  <TableCell>
                    <span
                      className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${appointment?.status === 'DIAGNOSED'
                        ? 'bg-green-50 text-green-600'
                        : appointment?.status === 'CANCELLED'
                          ? 'bg-red-50 text-red-600'
                          : 'bg-yellow-50 text-yellow-600'
                        }`}
                    >
                      {appointment.status}
                    </span>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap items-center gap-2">
                      {(appointment.status === AppointmentStatus.CONFIRMED) && (
                          <Link
                            to={`/doctor/consultation/${appointment?.patient?.id}/${appointment?.id}`}
                            className="inline-flex"
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={() => handleStartConsultation()}
                          >
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-green-600 hover:text-green-700 hover:bg-green-50"
                            >
                              Start Consultation
                            </Button>
                          </Link>
                        )}
                      {appointment.status === AppointmentStatus.DIAGNOSED && (
                        <>
                          {canEditDiagnosis && appointment.patient?.id && (
                            <Link
                              to={`/doctor/consultation/${appointment.patient.id}/${appointment.id}?edit=1`}
                              className="inline-flex"
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <Button
                                variant="outline"
                                size="sm"
                                className="text-teal-700 hover:text-teal-800 hover:bg-teal-50"
                              >
                                Edit Diagnosis
                              </Button>
                            </Link>
                          )}
                          <ViewDiagnosisRecordButton appointmentId={appointment.id} />
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="gap-1.5 text-green-700 hover:text-green-800 hover:bg-green-50"
                            disabled={isSendingWa}
                            onClick={() => handleSendWhatsApp(appointment)}
                            title={
                              appointment.patient?.phone
                                ? `Send prescription to ${appointment.patient.phone}`
                                : 'Patient has no phone number'
                            }
                          >
                            {isSendingWa ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <MessageCircle className="h-4 w-4" />
                            )}
                            {isSendingWa ? 'Sending…' : 'Send on WhatsApp'}
                          </Button>
                        </>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-2">
        <h1 className="font-artis-serif text-2xl font-semibold text-artis-brown-dark">My Appointments</h1>
      </div>
      <p className="text-sm text-gray-600 mb-6">
        Select any date to view that day’s appointments, including future bookings.
      </p>

      <div className="flex flex-wrap items-center gap-3 mb-6">
        <span className="text-sm font-medium text-gray-700">Select Date:</span>
        <DatePicker
          date={selectedDate}
          onDateChange={(next) => {
            if (next) {
              setSelectedDate(next);
            }
          }}
          className="w-[240px]"
        />
        <Button
          type="button"
          size="sm"
          variant={isToday ? 'default' : 'outline'}
          onClick={() => setSelectedDate(new Date())}
        >
          Today
        </Button>
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={() => setSelectedDate(addDays(new Date(), 1))}
        >
          Tomorrow
        </Button>
      </div>

      <div className="mt-6">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex" aria-label="Tabs">
            <button
              onClick={() => setActiveTab('upcoming')}
              className={`w-1/2 py-4 px-1 text-center border-b-2 font-medium text-sm ${activeTab === 'upcoming'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
            >
              {isToday ? "Today's Appointments" : 'Day Appointments'}
            </button>
            <button
              onClick={() => setActiveTab('completed')}
              className={`w-1/2 py-4 px-1 text-center border-b-2 font-medium text-sm ${activeTab === 'completed'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
            >
              Completed
            </button>
          </nav>
        </div>

        <div className="mt-6">
          {activeTab === 'upcoming' ? (
            <AppointmentTable
              appointments={upcomingAppointments}
              title={`Appointments — ${format(selectedDate, 'PPP')}`}
            />
          ) : (
            <AppointmentTable
              appointments={completedAppointments}
              title={`Completed — ${format(selectedDate, 'PPP')}`}
            />
          )}
        </div>
      </div>
    </div>
  );
}

export default Appointments;
